import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Row,
  Col,
  Statistic,
  DatePicker,
  Select,
  Space,
  Table,
  Spin,
  message,
  Empty,
  Radio,
  InputNumber,
  Tag,
  Progress,
  Tooltip,
  Button
} from 'antd';
import {
  UserOutlined,
  CheckCircleOutlined,
  ShoppingOutlined,
  PercentageOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  TeamOutlined,
  HomeOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { Column, Pie, Line } from '@ant-design/charts';
import axios from 'axios';
import dayjs from 'dayjs';
import authService from '../utils/auth';
import './Statistics.css';

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

function Statistics() {
  const [loading, setLoading] = useState(false);
  const [destinations, setDestinations] = useState([]);
  const [filters, setFilters] = useState({
    dateRange: [dayjs().subtract(3, 'month'), dayjs().add(3, 'month')],
    destinationId: null
  });
  const [activeTab, setActiveTab] = useState('overview');

  // 数据状态
  const [overviewData, setOverviewData] = useState(null);
  const [productData, setProductData] = useState([]);
  const [destinationData, setDestinationData] = useState([]);
  const [departureDateData, setDepartureDateData] = useState([]);
  const [nationalityData, setNationalityData] = useState([]);
  const [ageData, setAgeData] = useState([]);
  const [familyData, setFamilyData] = useState(null);
  const [dateGroupBy, setDateGroupBy] = useState('day');
  const [familyAgeRange, setFamilyAgeRange] = useState({ min: 30, max: 50 });

  // 加载目的地列表
  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const response = await axios.get(`${API_BASE}/destinations`, {
          headers: authService.getAuthHeaders()
        });
        if (response.data.success) {
          setDestinations(response.data.data || []);
        }
      } catch (error) {
        console.error('加载目的地失败:', error);
      }
    };
    fetchDestinations();
  }, []);

  // 根据当前标签页加载数据
  useEffect(() => {
    loadTabData(activeTab);
  }, [activeTab, filters, dateGroupBy, familyAgeRange]);

  const getFilterParams = () => {
    const params = {};
    if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
      params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
      params.endDate = filters.dateRange[1].format('YYYY-MM-DD');
    }
    if (filters.destinationId) {
      params.destinationId = filters.destinationId;
    }
    return params;
  };

  const loadTabData = async (tab) => {
    setLoading(true);
    try {
      const params = getFilterParams();

      switch (tab) {
        case 'overview':
          await loadOverview(params);
          break;
        case 'product':
          await loadProductStats(params);
          break;
        case 'destination':
          await loadDestinationStats(params);
          break;
        case 'departure':
          await loadDepartureDateStats({ ...params, groupBy: dateGroupBy });
          break;
        case 'nationality':
          await loadNationalityStats(params);
          break;
        case 'age':
          await loadAgeStats(params);
          break;
        case 'family':
          await loadFamilyStats({ ...params, minAge: familyAgeRange.min, maxAge: familyAgeRange.max });
          break;
        default:
          break;
      }
    } catch (error) {
      message.error('加载数据失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadOverview = async (params) => {
    const response = await axios.get(`${API_BASE}/statistics/overview`, {
      params,
      headers: authService.getAuthHeaders()
    });
    if (response.data.success) {
      setOverviewData(response.data.data);
    }
  };

  const loadProductStats = async (params) => {
    const response = await axios.get(`${API_BASE}/statistics/by-product`, {
      params,
      headers: authService.getAuthHeaders()
    });
    if (response.data.success) {
      setProductData(response.data.data || []);
    }
  };

  const loadDestinationStats = async (params) => {
    const response = await axios.get(`${API_BASE}/statistics/by-destination`, {
      params,
      headers: authService.getAuthHeaders()
    });
    if (response.data.success) {
      setDestinationData(response.data.data || []);
    }
  };

  const loadDepartureDateStats = async (params) => {
    const response = await axios.get(`${API_BASE}/statistics/by-departure-date`, {
      params,
      headers: authService.getAuthHeaders()
    });
    if (response.data.success) {
      setDepartureDateData(response.data.data || []);
    }
  };

  const loadNationalityStats = async (params) => {
    const response = await axios.get(`${API_BASE}/statistics/nationality-distribution`, {
      params,
      headers: authService.getAuthHeaders()
    });
    if (response.data.success) {
      setNationalityData(response.data.data || []);
    }
  };

  const loadAgeStats = async (params) => {
    const response = await axios.get(`${API_BASE}/statistics/age-distribution`, {
      params,
      headers: authService.getAuthHeaders()
    });
    if (response.data.success) {
      setAgeData(response.data.data || []);
    }
  };

  const loadFamilyStats = async (params) => {
    const response = await axios.get(`${API_BASE}/statistics/family-age-stats`, {
      params,
      headers: authService.getAuthHeaders()
    });
    if (response.data.success) {
      setFamilyData(response.data.data);
    }
  };

  const handleDateRangeChange = (dates) => {
    setFilters(prev => ({ ...prev, dateRange: dates }));
  };

  const handleDestinationChange = (value) => {
    setFilters(prev => ({ ...prev, destinationId: value }));
  };

  // 导出家庭年龄数据为CSV
  const handleExportFamilyData = () => {
    if (!familyData || !familyData.familyDetails || familyData.familyDetails.length === 0) {
      message.warning('暂无数据可导出');
      return;
    }

    const { summary, familyDetails } = familyData;

    // CSV表头
    const csvHeaders = ['EKOK', '产品名称', '出发日期', '家庭人数', '成员姓名', '成员年龄', '是否在目标年龄段'];

    // 构建CSV内容 - 每个成员一行
    const csvRows = [];
    familyDetails.forEach(family => {
      family.members.forEach((member, index) => {
        const isInTargetAge = member.age !== '未知' && member.age >= familyAgeRange.min && member.age <= familyAgeRange.max;
        csvRows.push([
          index === 0 ? family.ekok : '', // 只在第一行显示EKOK
          index === 0 ? family.productName : '',
          index === 0 ? dayjs(family.departureDate).format('YYYY-MM-DD') : '',
          index === 0 ? family.memberCount : '',
          member.name || '',
          member.age === '未知' ? '未知' : member.age,
          isInTargetAge ? '是' : '否'
        ].map(field => `"${field}"`).join(','));
      });
    });

    const csvContent = [
      csvHeaders.join(','),
      ...csvRows
    ].join('\n');

    // 添加统计摘要作为CSV末尾
    const summaryContent = [
      '',
      '"统计摘要"',
      `"总家庭数",${summary.totalFamilies}`,
      `"有${summary.ageRange}成员的家庭数",${summary.familiesWithTargetAge}`,
      `"无${summary.ageRange}成员的家庭数",${summary.familiesWithoutTargetAge}`,
      `"${summary.ageRange}家庭占比",${summary.percentage}%`
    ].join('\n');

    const fullContent = csvContent + '\n' + summaryContent;

    // 创建下载
    const blob = new Blob(['\uFEFF' + fullContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `家庭年龄统计_${familyAgeRange.min}-${familyAgeRange.max}岁_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success(`已导出 ${familyDetails.length} 个家庭的数据`);
  };

  // 渲染概览卡片
  const renderOverview = () => {
    if (!overviewData) {
      return <Empty description="暂无数据" />;
    }

    return (
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={12} md={8} lg={4}>
          <Card className="stat-card">
            <Statistic
              title="总游客数"
              value={overviewData.totalTourists}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={8} lg={4}>
          <Card className="stat-card">
            <Statistic
              title="已验证"
              value={overviewData.verifiedTourists}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={8} lg={4}>
          <Card className="stat-card">
            <Statistic
              title="待处理"
              value={overviewData.pendingTourists}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={8} lg={4}>
          <Card className="stat-card">
            <Statistic
              title="已驳回"
              value={overviewData.rejectedTourists}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={8} lg={4}>
          <Card className="stat-card">
            <Statistic
              title="产品数"
              value={overviewData.totalProducts}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={8} lg={4}>
          <Card className="stat-card">
            <Statistic
              title="验证率"
              value={overviewData.verificationRate}
              suffix="%"
              prefix={<PercentageOutlined />}
              valueStyle={{ color: overviewData.verificationRate >= 80 ? '#52c41a' : '#faad14' }}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  // 渲染按产品统计
  const renderProductStats = () => {
    if (productData.length === 0) {
      return <Empty description="暂无数据" />;
    }

    const chartConfig = {
      data: productData.slice(0, 20), // 只显示前20个
      xField: 'productName',
      yField: 'touristCount',
      label: {
        text: (d) => d.touristCount,
        textBaseline: 'bottom',
      },
      style: {
        fill: '#1890ff',
      },
      axis: {
        x: {
          labelAutoRotate: true,
        },
      },
    };

    const columns = [
      {
        title: '排名',
        key: 'rank',
        width: 60,
        render: (_, __, index) => index + 1
      },
      {
        title: '产品名称',
        dataIndex: 'productName',
        key: 'productName',
        ellipsis: true
      },
      {
        title: '目的地',
        dataIndex: 'destination',
        key: 'destination',
        width: 120
      },
      {
        title: '出发日期',
        dataIndex: 'departureDate',
        key: 'departureDate',
        width: 120,
        render: (date) => dayjs(date).format('YYYY-MM-DD')
      },
      {
        title: '游客人数',
        dataIndex: 'touristCount',
        key: 'touristCount',
        width: 100,
        sorter: (a, b) => a.touristCount - b.touristCount
      }
    ];

    return (
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="游客人数柱状图（Top 20）" className="chart-card">
            <Column {...chartConfig} height={300} />
          </Card>
        </Col>
        <Col span={24}>
          <Card title="产品明细" className="table-card">
            <Table
              dataSource={productData}
              columns={columns}
              rowKey="productId"
              size="small"
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total) => `共 ${total} 条`
              }}
              scroll={{ x: 600 }}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  // 渲染按目的地统计
  const renderDestinationStats = () => {
    if (destinationData.length === 0) {
      return <Empty description="暂无数据" />;
    }

    const pieData = destinationData.filter(d => d.touristCount > 0);

    const pieConfig = {
      data: pieData,
      angleField: 'touristCount',
      colorField: 'destinationName',
      label: {
        text: (d) => `${d.destinationName}: ${d.percentage}%`,
        style: {
          fontWeight: 'bold',
        },
      },
      legend: {
        color: {
          title: false,
          position: 'right',
          rowPadding: 5,
        },
      },
    };

    const columns = [
      {
        title: '排名',
        key: 'rank',
        width: 60,
        render: (_, __, index) => index + 1
      },
      {
        title: '目的地',
        dataIndex: 'destinationName',
        key: 'destinationName'
      },
      {
        title: '产品数',
        dataIndex: 'productCount',
        key: 'productCount',
        width: 100
      },
      {
        title: '游客人数',
        dataIndex: 'touristCount',
        key: 'touristCount',
        width: 100,
        sorter: (a, b) => a.touristCount - b.touristCount
      },
      {
        title: '占比',
        dataIndex: 'percentage',
        key: 'percentage',
        width: 80,
        render: (val) => `${val}%`
      }
    ];

    return (
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="目的地分布饼图" className="chart-card">
            {pieData.length > 0 ? (
              <Pie {...pieConfig} height={350} />
            ) : (
              <Empty description="暂无数据" />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="目的地明细" className="table-card">
            <Table
              dataSource={destinationData}
              columns={columns}
              rowKey="destinationId"
              size="small"
              pagination={false}
              scroll={{ x: 400 }}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  // 渲染按出发日期统计
  const renderDepartureDateStats = () => {
    if (departureDateData.length === 0) {
      return <Empty description="暂无数据" />;
    }

    const lineConfig = {
      data: departureDateData,
      xField: 'date',
      yField: 'touristCount',
      point: {
        shapeField: 'circle',
        sizeField: 4,
      },
      interaction: {
        tooltip: {
          marker: false,
        },
      },
      style: {
        lineWidth: 2,
      },
    };

    return (
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card
            title="出发日期趋势"
            extra={
              <Radio.Group
                value={dateGroupBy}
                onChange={(e) => setDateGroupBy(e.target.value)}
                size="small"
              >
                <Radio.Button value="day">按天</Radio.Button>
                <Radio.Button value="week">按周</Radio.Button>
                <Radio.Button value="month">按月</Radio.Button>
              </Radio.Group>
            }
            className="chart-card"
          >
            <Line {...lineConfig} height={350} />
          </Card>
        </Col>
      </Row>
    );
  };

  // 渲染国籍分布
  const renderNationalityStats = () => {
    if (nationalityData.length === 0) {
      return <Empty description="暂无数据" />;
    }

    const pieData = nationalityData.filter(d => d.count > 0);

    const pieConfig = {
      data: pieData,
      angleField: 'count',
      colorField: 'nationality',
      label: {
        text: (d) => `${d.nationality}: ${d.percentage}%`,
        style: {
          fontWeight: 'bold',
        },
      },
      legend: {
        color: {
          title: false,
          position: 'right',
          rowPadding: 5,
        },
      },
    };

    const columns = [
      {
        title: '排名',
        key: 'rank',
        width: 60,
        render: (_, __, index) => index + 1
      },
      {
        title: '国籍',
        dataIndex: 'nationality',
        key: 'nationality'
      },
      {
        title: '人数',
        dataIndex: 'count',
        key: 'count',
        width: 100,
        sorter: (a, b) => a.count - b.count
      },
      {
        title: '占比',
        dataIndex: 'percentage',
        key: 'percentage',
        width: 80,
        render: (val) => `${val}%`
      }
    ];

    return (
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="国籍分布饼图" className="chart-card">
            {pieData.length > 0 ? (
              <Pie {...pieConfig} height={350} />
            ) : (
              <Empty description="暂无数据" />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="国籍明细" className="table-card">
            <Table
              dataSource={nationalityData}
              columns={columns}
              rowKey="nationality"
              size="small"
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total) => `共 ${total} 条`
              }}
              scroll={{ x: 300 }}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  // 渲染年龄分布
  const renderAgeStats = () => {
    if (ageData.length === 0) {
      return <Empty description="暂无数据" />;
    }

    const chartConfig = {
      data: ageData,
      xField: 'ageGroup',
      yField: 'count',
      label: {
        text: (d) => d.count,
        textBaseline: 'bottom',
      },
      style: {
        fill: '#722ed1',
      },
    };

    const columns = [
      {
        title: '年龄段',
        dataIndex: 'ageGroup',
        key: 'ageGroup'
      },
      {
        title: '人数',
        dataIndex: 'count',
        key: 'count',
        width: 100,
        sorter: (a, b) => a.count - b.count
      },
      {
        title: '占比',
        dataIndex: 'percentage',
        key: 'percentage',
        width: 80,
        render: (val) => `${val}%`
      }
    ];

    return (
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card title="年龄分布柱状图" className="chart-card">
            <Column {...chartConfig} height={350} />
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="年龄明细" className="table-card">
            <Table
              dataSource={ageData}
              columns={columns}
              rowKey="ageGroup"
              size="small"
              pagination={false}
              scroll={{ x: 250 }}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  // 渲染家庭年龄统计
  const renderFamilyStats = () => {
    if (!familyData) {
      return <Empty description="暂无数据" />;
    }

    const { summary, sizeDistribution, familyDetails } = familyData;

    const sizeChartConfig = {
      data: sizeDistribution,
      xField: 'size',
      yField: 'count',
      label: {
        text: (d) => d.count,
        textBaseline: 'bottom',
      },
      style: {
        fill: '#13c2c2',
      },
    };

    const familyColumns = [
      {
        title: 'EKOK',
        dataIndex: 'ekok',
        key: 'ekok',
        width: 120
      },
      {
        title: '产品',
        dataIndex: 'productName',
        key: 'productName',
        ellipsis: true,
        width: 150
      },
      {
        title: '出发日期',
        dataIndex: 'departureDate',
        key: 'departureDate',
        width: 110,
        render: (date) => dayjs(date).format('YYYY-MM-DD')
      },
      {
        title: '人数',
        dataIndex: 'memberCount',
        key: 'memberCount',
        width: 60
      },
      {
        title: '家庭成员年龄',
        dataIndex: 'members',
        key: 'members',
        render: (members) => (
          <Space size={[4, 4]} wrap>
            {members.map((m, idx) => {
              const age = m.age;
              const isTarget = age !== '未知' && age >= familyAgeRange.min && age <= familyAgeRange.max;
              return (
                <Tooltip key={idx} title={m.name}>
                  <Tag color={isTarget ? 'green' : (age === '未知' ? 'default' : 'blue')}>
                    {age === '未知' ? '?' : age}岁
                  </Tag>
                </Tooltip>
              );
            })}
          </Space>
        )
      }
    ];

    return (
      <Row gutter={[16, 16]}>
        {/* 年龄范围设置 */}
        <Col span={24}>
          <Card size="small" className="age-range-card">
            <Space wrap>
              <span>统计年龄范围：</span>
              <InputNumber
                min={0}
                max={100}
                value={familyAgeRange.min}
                onChange={(val) => setFamilyAgeRange(prev => ({ ...prev, min: val || 0 }))}
                style={{ width: 80 }}
              />
              <span>-</span>
              <InputNumber
                min={0}
                max={100}
                value={familyAgeRange.max}
                onChange={(val) => setFamilyAgeRange(prev => ({ ...prev, max: val || 100 }))}
                style={{ width: 80 }}
              />
              <span>岁</span>
            </Space>
          </Card>
        </Col>

        {/* 概览卡片 */}
        <Col xs={12} sm={12} md={6}>
          <Card className="stat-card">
            <Statistic
              title="总家庭数"
              value={summary.totalFamilies}
              prefix={<HomeOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card className="stat-card">
            <Statistic
              title={`有${summary.ageRange}成员`}
              value={summary.familiesWithTargetAge}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card className="stat-card">
            <Statistic
              title={`无${summary.ageRange}成员`}
              value={summary.familiesWithoutTargetAge}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card className="stat-card highlight-card">
            <Statistic
              title={`${summary.ageRange}家庭占比`}
              value={summary.percentage}
              suffix="%"
              prefix={<PercentageOutlined />}
              valueStyle={{ color: '#722ed1', fontSize: 28 }}
            />
            <Progress
              percent={summary.percentage}
              showInfo={false}
              strokeColor="#722ed1"
              style={{ marginTop: 8 }}
            />
          </Card>
        </Col>

        {/* 家庭规模分布 */}
        <Col xs={24} lg={10}>
          <Card title="家庭规模分布" className="chart-card">
            {sizeDistribution.length > 0 ? (
              <Column {...sizeChartConfig} height={300} />
            ) : (
              <Empty description="暂无数据" />
            )}
          </Card>
        </Col>

        {/* 家庭规模明细表 */}
        <Col xs={24} lg={14}>
          <Card
            title={`含${summary.ageRange}成员的家庭明细`}
            className="table-card"
            extra={
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExportFamilyData}
                size="small"
              >
                导出CSV
              </Button>
            }
          >
            <Table
              dataSource={familyDetails}
              columns={familyColumns}
              rowKey="ekok"
              size="small"
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total) => `共 ${total} 条`
              }}
              scroll={{ x: 600 }}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  const tabItems = [
    {
      key: 'overview',
      label: '数据概览',
      children: renderOverview()
    },
    {
      key: 'product',
      label: '按产品统计',
      children: renderProductStats()
    },
    {
      key: 'destination',
      label: '按目的地统计',
      children: renderDestinationStats()
    },
    {
      key: 'departure',
      label: '按出发日期统计',
      children: renderDepartureDateStats()
    },
    {
      key: 'nationality',
      label: '国籍分布',
      children: renderNationalityStats()
    },
    {
      key: 'age',
      label: '年龄分布',
      children: renderAgeStats()
    },
    {
      key: 'family',
      label: '家庭年龄统计',
      children: renderFamilyStats()
    }
  ];

  return (
    <div className="statistics-page">
      <Card className="filter-card">
        <Space wrap size="middle">
          <span>日期范围：</span>
          <RangePicker
            value={filters.dateRange}
            onChange={handleDateRangeChange}
            allowClear
            format="YYYY-MM-DD"
          />
          <span>目的地：</span>
          <Select
            value={filters.destinationId}
            onChange={handleDestinationChange}
            allowClear
            placeholder="全部目的地"
            style={{ width: 150 }}
          >
            {destinations.map(d => (
              <Select.Option key={d.id} value={d.id}>
                {d.name}
              </Select.Option>
            ))}
          </Select>
        </Space>
      </Card>

      <Spin spinning={loading}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className="statistics-tabs"
        />
      </Spin>
    </div>
  );
}

export default Statistics;
